import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import type { MeResponse } from "./api/me";
import { getBranding } from "./api/reports";
import { resolveTenant } from "./api/resolve";
import { ApiError } from "./api/client";
import { applyBranding } from "./branding";
import type { TenantBranding } from "./types";
import { ToastHost } from "./components/Toast";

// Venture (end-user) screens
import { VentureSignIn } from "./venture/SignIn";
import { VentureHub } from "./venture/Hub";
import { VenturePage } from "./venture/Page";
import { VentureDenied } from "./venture/Denied";

// Admin screens
import { AdminLayout } from "./admin/Layout";
import { AdminHome } from "./admin/Home";
import { AdminPeople } from "./admin/People";
import { AdminPersonDetail } from "./admin/PersonDetail";
import { AdminPages } from "./admin/Pages";
import { AdminPageDetail } from "./admin/PageDetail";
import { AdminBranding } from "./admin/Branding";
import { AdminAudit } from "./admin/Audit";
import { SuperTenantsList } from "./admin/SuperTenants";
import { SuperTenantDetail } from "./admin/SuperTenantDetail";

interface AppState {
  me: MeResponse | null;
  branding: TenantBranding | null;
  tenantSlug: string | null;
  error: string | null;
  loading: boolean;
  // True when /api/me returned 401 — render the branded sign-in screen
  // instead of silently bouncing to /auth/google/login. We still load
  // branding first so the sign-in is themed.
  signedOut: boolean;
}

export default function App() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<AppState>({
    me: null,
    branding: null,
    tenantSlug: null,
    error: null,
    loading: true,
    signedOut: false,
  });

  useEffect(() => {
    void (async () => {
      try {
        // 1. Resolve tenant from Host (or ?tenant=) BEFORE any auth call,
        //    so the sign-in screen can be branded.
        const resolved = await resolveTenant(searchParams.get("tenant"));
        const branding = await getBranding(resolved.tenant_slug);
        applyBranding(branding);

        // 2. Try /api/me. If 401, render branded sign-in instead of redirecting.
        //    (Bypass the auto-redirect helper so SignIn renders.)
        const r = await fetch("/api/me", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (r.status === 401) {
          setState({
            me: null,
            branding,
            tenantSlug: resolved.tenant_slug,
            error: null,
            loading: false,
            signedOut: true,
          });
          return;
        }
        if (!r.ok) {
          throw new ApiError(r.status, await r.text());
        }
        const me = (await r.json()) as MeResponse;

        setState({
          me,
          branding,
          tenantSlug: resolved.tenant_slug,
          error: null,
          loading: false,
          signedOut: false,
        });
      } catch (e) {
        const msg = e instanceof ApiError ? e.detail : (e as Error).message;
        setState((s) => ({ ...s, loading: false, error: msg }));
      }
    })();
  }, [searchParams]);

  if (state.loading) {
    return <div className="shell" style={{ padding: 24 }}>Loading…</div>;
  }

  // Couldn't even resolve a tenant → can't render anything branded.
  // Show an instructive screen so the operator knows the recovery path
  // instead of staring at the raw API detail.
  if (state.error && !state.branding) {
    return (
      <div className="shell" style={{ padding: 24, maxWidth: 640, margin: "0 auto" }}>
        <ToastHost />
        <h1>Reports isn't configured for this URL</h1>
        <p style={{ color: "var(--ink-3)" }}>
          The Reports app couldn't identify which venture you're trying to use.
        </p>
        <p style={{ color: "var(--ink-3)" }}>
          If you're an operator: either visit a configured custom domain
          (e.g. <code>dashboard.builtwithfoundry.com</code>), set the{" "}
          <code>DEFAULT_TENANT_SLUG</code> env var on the service, or append{" "}
          <code>?tenant=&lt;slug&gt;</code> to the URL.
        </p>
        <details>
          <summary style={{ cursor: "pointer", color: "var(--ink-4)" }}>
            Technical details
          </summary>
          <pre style={{ color: "var(--ink-4)", fontSize: 12, whiteSpace: "pre-wrap" }}>
            {state.error}
          </pre>
        </details>
      </div>
    );
  }

  // Branded sign-in (no session yet)
  if (state.signedOut && state.branding) {
    return (
      <div className="shell">
        <ToastHost />
        <VentureSignIn branding={state.branding} />
      </div>
    );
  }

  if (state.error || !state.me || !state.branding || !state.tenantSlug) {
    return (
      <div className="shell" style={{ padding: 24 }}>
        <ToastHost />
        <h1>Couldn't load Reports</h1>
        <p style={{ color: "var(--ink-3)" }}>{state.error ?? "Unknown error"}</p>
      </div>
    );
  }

  const { me, branding, tenantSlug } = state;
  const myTenant = me.tenants.find((t) => t.tenant_slug === tenantSlug);
  if (!myTenant) {
    // Signed in, but not a member of this tenant.
    return (
      <div className="shell" style={{ padding: 24 }}>
        <ToastHost />
        <h1>No access</h1>
        <p style={{ color: "var(--ink-3)" }}>
          You're signed in as <b>{me.email}</b> but not a member of{" "}
          <b>{branding.display_name}</b>. Ask the admin to invite you.
        </p>
      </div>
    );
  }
  const isAdmin = myTenant.role === "admin" || myTenant.role === "owner";
  // Super-admin gate is checked server-side too; this is just for UI affordances.
  // OWNER on foundry-self → can see /admin/super.
  const isFoundryOwner = me.tenants.some(
    (t) => t.tenant_slug === "foundry-self" && t.role === "owner",
  );

  return (
    <div className="shell">
      <ToastHost />
      <Routes>
        {/* Venture (end-user) ───────────── */}
        <Route
          index
          element={
            <VentureHub
              branding={branding}
              tenantSlug={tenantSlug}
              me={me}
              isAdmin={isAdmin}
            />
          }
        />
        <Route
          path="/page/:pageId"
          element={
            <VenturePageRoute
              branding={branding}
              tenantSlug={tenantSlug}
              me={me}
              isAdmin={isAdmin}
            />
          }
        />
        <Route
          path="/denied"
          element={<VentureDenied branding={branding} />}
        />

        {/* Admin ───────────────────────── */}
        {isAdmin && (
          <Route
            path="/admin"
            element={<AdminLayout branding={branding} tenantSlug={tenantSlug} isFoundryOwner={isFoundryOwner} />}
          >
            <Route index element={<AdminHome branding={branding} tenantSlug={tenantSlug} />} />
            <Route
              path="people"
              element={<AdminPeople tenantSlug={tenantSlug} />}
            />
            <Route
              path="people/:memberId"
              element={<AdminPersonDetail tenantSlug={tenantSlug} />}
            />
            <Route path="pages" element={<AdminPages tenantSlug={tenantSlug} />} />
            <Route
              path="pages/:pageId"
              element={<AdminPageDetail tenantSlug={tenantSlug} />}
            />
            <Route
              path="branding"
              element={<AdminBranding branding={branding} />}
            />
            <Route
              path="audit"
              element={<AdminAudit tenantSlug={tenantSlug} />}
            />
            {isFoundryOwner && (
              <>
                <Route path="super" element={<SuperTenantsList />} />
                <Route path="super/:slug" element={<SuperTenantDetail />} />
              </>
            )}
          </Route>
        )}

        {/* Non-admin trying to reach /admin → home */}
        {!isAdmin && <Route path="/admin/*" element={<Navigate to="/" replace />} />}

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

interface VenturePageRouteProps {
  branding: TenantBranding;
  tenantSlug: string;
  me: MeResponse;
  isAdmin: boolean;
}

function VenturePageRoute({ branding, tenantSlug, me, isAdmin }: VenturePageRouteProps) {
  const { pageId } = useParams<{ pageId: string }>();
  const id = Number(pageId);
  if (!Number.isFinite(id)) return <Navigate to="/denied" replace />;
  return (
    <VenturePage
      branding={branding}
      tenantSlug={tenantSlug}
      me={me}
      pageId={id}
      isAdmin={isAdmin}
    />
  );
}
